import { terms, type Term } from './terms';

/**
 * Resolution of `{{term key.variant}}` tags in text that arrives at runtime.
 *
 * Code should almost never come here: a Lit template interpolates `terms.loginGroup.plural`
 * directly and gets compile-time safety. This path exists for text the compiler cannot reach -
 * database content and configuration, principally the server-composed audit descriptions described
 * in `terms-and-vocabulary.md` section 5.
 *
 * Because those strings arrive at runtime, type safety cannot help, so failure has to be loud
 * instead. D-028 sets the shape of that: a marker, never a throw.
 */

/**
 * Rendered in place of a tag that does not resolve.
 *
 * `MISSING TERM` is plain ASCII deliberately, so a Playwright assertion, CI output and `grep` can
 * all find it; the guillemets make it unmissable in a screenshot or a recorded video. It cannot
 * occur in real product copy, which is what lets one shared test fixture police every screen.
 */
export const MISSING_TERM_SENTINEL = 'MISSING TERM';

/** Builds the marker for a tag that failed to resolve. */
export function missingTermMarker(path: string): string {
  return `⟪${MISSING_TERM_SENTINEL}: ${path}⟫`;
}

/**
 * Looks up one dotted `key.variant` path against the typed terms object.
 *
 * Returns undefined rather than throwing for ANY failure, because this runs while rendering stored
 * text that may be historically stale: old rows are deliberately never rewritten (D-027), so a
 * retired key must degrade one row rather than take out the page it appears on.
 */
export function resolveTermPath(path: string): string | undefined {
  const [key, variant] = path.split('.');
  if (!key || !variant) return undefined;

  const termsByKey = terms as Record<string, Term | undefined>;
  const term = termsByKey[key];
  if (!term) return undefined;

  const value = (term as unknown as Record<string, unknown>)[variant];
  return typeof value === 'string' ? value : undefined;
}

/** True when the path names a term and a variant that both exist. Used by the guard tests. */
export function termPathResolves(path: string): boolean {
  return resolveTermPath(path) !== undefined;
}
